import { type ChatMessage } from "@do/shared-types";

/**
 * Highest message number handed out so far in this conversation.
 *
 * Message ids are `message-<n>`, and `n` used to be re-derived from the length of the
 * `messages` array on every change. That is wrong in `onlyLastMessage` mode, where the array
 * is REPLACED by a single message on each exchange: the counter falls back to 0, and the next
 * message is handed an id the message currently on screen already carries.
 *
 * Two messages sharing an id share a React key, so the conversation reconciles them as one
 * node — the outgoing message never leaves, the incoming one never arrives, and neither
 * animates. It also puts the same DOM id on two elements at once.
 *
 * Keeping the high-water mark makes ids strictly increasing for the life of a conversation.
 * Starting a new one resets the counter explicitly.
 */
export const syncMessageSequence = (current: number, messages: ChatMessage[]): number =>
  Math.max(
    current,
    // The streaming placeholder is transient and never keeps its id.
    messages.filter((message) => message.id && message.id !== "streamed-msg").length
  );
